import React, { useMemo } from 'react';
import { CompanyRow, PageInfo, SearchFilter, SelectFilter, SupplierListModel } from './type';
import { useMemoizedFn } from 'ahooks';
import { searchCardIdListPost } from '@/services/swagger/easourcing/SessionService';
import {
  IApiLanguageDto,
  IApiSessionSearchCardOtherReq,
  IApiSubMessageDto,
} from '@/services/swagger/easourcing/models';
import { SearchCardSubMsg } from '../../types';
import { useChatPageStore, useSupplierListStore } from '../../store';
import { useShallow } from 'zustand/shallow';
import { toast } from '@leadsnavi/ui-kit';
import { isObject } from 'lodash-es';

import { Storage } from '@/utils/storage';
export const autoClickedKey = 'autoClicked';
export type AutoClickKeyValue = {
  isOpend: boolean;
};
type SupplierListParams = Pick<Required<IApiSessionSearchCardOtherReq>, 'sessionId' | 'messageId' | 'subMessageId'> & {
  subMsgData: IApiSubMessageDto;
};

export const joinLanguage = (language: IApiLanguageDto) => {
  return language.language + ' - ' + language.product;
};
export const splitLanguage = (language: string): IApiLanguageDto => {
  const [languageName, productName] = language.split(' - ');
  return {
    language: languageName,
    product: productName,
  };
};

const useSubMsgData = (subMsgData: IApiSubMessageDto) => {
  const originalSearchCard = useMemo<Partial<SearchCardSubMsg['contentValue']['originalSearchCard']>>(() => {
    const subMsg = subMsgData as SearchCardSubMsg;
    if (!subMsg) {
      return {};
    }
    try {
      if (isObject(subMsg.contentValue.originalSearchCard)) {
        return subMsg.contentValue.originalSearchCard;
      }
      return JSON.parse(subMsg.contentValue.originalSearchCard as unknown as string);
    } catch (error) {
      console.warn('parse contentValue error', error);
      return {};
    }
  }, [subMsgData]);
  const otherLanguages = useMemo(() => {
    return originalSearchCard?.otherLanguage || [];
  }, [originalSearchCard]);
  return {
    originalSearchCard,
    otherLanguages,
  };
};

export const useSupplierListModel = (params: SupplierListParams): SupplierListModel => {
  const { messageId, sessionId, subMessageId, subMsgData } = params;
  const { otherLanguages } = useSubMsgData(subMsgData);
  const [isFirstLoading, setIsFirstLoading] = React.useState(true);
  const _selectFilter = useChatPageStore(useShallow(store => store.selectedSuppliers[subMessageId]?.selectFilter));
  const selectFilter = useMemo(() => {
    return (
      _selectFilter || {
        isSelectAll: false,
        excludeIds: [],
        includeIds: [],
      }
    );
  }, [_selectFilter]);
  const [visibleDetail, setVisibleDetail] = React.useState<CompanyRow | null>(null);
  const [productTranslateData] = React.useState<Array<IApiLanguageDto>>(otherLanguages);
  const [searchFilter, setSearchFilter] = React.useState<SearchFilter>({
    otherLanguages: [],
    country: [],
    productDetail: [],
    filterEdm: false,
  });
  const getStoreState = useChatPageStore(state => state.getStoreState);
  const setStoreState = useChatPageStore(state => state.setStoreState);

  // 创建 supplier list store 实例

  // 使用 supplier list store
  const { aggregatedSuppliers, supplierListData, isOtherDataLoading, isSummaryLoading, getSupplierList, lastParams } =
    useSupplierListStore(
      useShallow(store => ({
        aggregatedSuppliers: store.aggregatedSuppliers,
        supplierListData: store.supplierListData,
        isLoading: store.isLoading,
        isOtherDataLoading: store.isOtherDataLoading,
        isSummaryLoading: store.isSummaryLoading,
        getSupplierList: store.getSupplierList,
        lastParams: store.lastParams,
      }))
    );

  // /**
  //  * 调用产品翻译接口
  //  */
  // const fetchProductTranslate = useMemoizedFn(async () => {
  //   return productTranslatePost({
  //     language: LANGUAGES,
  //     product: subMsgData.contentValue.queryProduct,
  //   }).then(res => res.data.language);
  // });
  /**
   * 产品翻译语言选项
   */
  const productLanguageOptions = useMemo(() => {
    return (
      productTranslateData?.map(item => ({
        label: joinLanguage(item),
        value: joinLanguage(item),
      })) || []
    );
  }, [productTranslateData]);

  const searchSelectedLanguages = useMemo(() => {
    return searchFilter.otherLanguages?.map(joinLanguage) || [];
  }, [searchFilter]);
  const addSelectedSupplier = useChatPageStore(useShallow(store => store.addSelectedSupplier));
  const deleteSelectedSupplier = useChatPageStore(useShallow(store => store.deleteSelectedSupplier));
  const setSelectFilter = useMemoizedFn((selectFilter: SelectFilter) => {
    // 如果有选择的供应商，则更新到store里
    if (selectFilter?.includeIds?.length) {
      const isOverLimit = selectFilter?.includeIds?.length > 2000;
      if (isOverLimit) {
        toast.warning(`You can select up to 2000 suppliers`);
        return;
      }
      addSelectedSupplier(subMessageId, {
        selectFilter: selectFilter,
        subMsg: subMsgData as unknown as SearchCardSubMsg,
        messageId,
      });
    } else {
      deleteSelectedSupplier(subMessageId);
    }
  });

  const onSearchChange = useMemoizedFn((filter: Partial<SearchFilter>) => {
    console.log('onSearchChange', filter);
    setSearchFilter({
      ...searchFilter,
      ...filter,
    });
    getSupplierList(
      {
        ...filter,
        page: 1,
        sessionId,
        messageId,
        subMessageId,
      },
      { isFilterChange: true }
    );
    setSelectFilter({
      includeIds: [],
    });
  });
  const getSuppliersByDetailInit = useMemoizedFn(() => {
    getSupplierList(
      {
        page: 1,
        pageSize: 20,
      },
      { isFilterChange: true }
    );
  });

  // 聚合供应商列表数据（使用 store 中的聚合数据）
  const companyList = aggregatedSuppliers;

  const pageInfo: PageInfo = {
    pageNo: supplierListData?.page || 1,
    pageSize: supplierListData?.pageSize || 20,
    totalCount: supplierListData?.total || 0,
  };

  const onSelectAllClick = useMemoizedFn(async () => {
    const res = await searchCardIdListPost({
      messageId,
      sessionId,
      subMessageId,
      country: lastParams.country,
      otherLanguages: lastParams.otherLanguages,
      productDetail: lastParams.productDetail,
      filterEdm: lastParams.filterEdm,
      page: pageInfo.pageNo,
      pageSize: pageInfo.pageSize,
    });
    const ids = res.data.globalSearchIdList;
    setSelectFilter({
      includeIds: ids,
    });
  });
  const getSupplierListByInit = useMemoizedFn(async () => {
    // 3. 设置搜索过滤条件
    setSearchFilter({
      country: subMsgData.contentValue.country,
      otherLanguages: otherLanguages,
    });

    // 4. 获取供应商列表
    await getSupplierList(
      {
        sessionId: sessionId,
        subMessageId: subMsgData.subMessageId,
        messageId: messageId,
        country: subMsgData.contentValue.country,
        // 默认所有语言
        otherLanguages: otherLanguages,
      },
      { isFilterChange: true }
    );
    // 5. 设置加载状态为false
    setIsFirstLoading(false);

    // 6. 如果是 sse 消息（ai 回答的消息），则需要默认全选
    const isSseMsg = getStoreState().sseMessageIds.has(messageId);
    if (isSseMsg) {
      onSelectAllClick();
      const isOpenAutoClick = Storage.getItem<AutoClickKeyValue>(autoClickedKey)?.isOpend;
      console.log('isOpenAutoClick', isOpenAutoClick);
      if (!isOpenAutoClick) {
        setStoreState({
          isOpenAutoClick: true,
        });
      }
      return;
    }
  });

  const handlePageChange = useMemoizedFn((page: number) => {
    getSupplierList(
      {
        page,
      },
      { isFilterChange: false }
    );
  });
  return {
    showSkeleton: isFirstLoading,
    isAiSummaryLoding: isSummaryLoading,
    isKeywordLoading: isOtherDataLoading,
    suppliers: companyList,
    languageOptions: productLanguageOptions,
    searchSelectedLanguages,
    visibleDetail,
    onVisibleDetailChange: setVisibleDetail,
    onSelectChange: setSelectFilter,
    onSearchChange,
    searchFilter,
    pageInfo,
    selectFilter,
    onDetailClick: setVisibleDetail,
    getSupplierListByInit,
    onSelectAllClick,
    handlePageChange,
    getSuppliersByDetailInit,
  };
};
